import React, { useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Button,
  Card,
  SegmentedControl,
  Select,
  Stack,
  Title,
} from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";

import { BasicForm } from "@components/shared";
import { notifyUserFacingError } from "@util/toErrorMessage";
import {
  ORGANIZATION_MEMBER_CONFIG,
  fetchMembers,
  fetchOrgUsers,
  membersQueryKey,
  orgJoinSelectLabel,
} from "./organizationMemberShared";

export default function OrganizationMemberForm({
  organizationId,
  type = "users",
  onSuccess = () => {},
}) {
  const { t } = useTranslation();
  const config = ORGANIZATION_MEMBER_CONFIG[type];
  const [userMode, setUserMode] = useState("add");
  const [formKey, setFormKey] = useState(0);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const orgUsersQuery = useQuery({
    queryKey: [...membersQueryKey(organizationId, "users"), "options"],
    queryFn: ({ signal }) => fetchOrgUsers(organizationId, signal),
    enabled: type !== "users",
  });

  const assignedIdsQuery = useQuery({
    queryKey: [...membersQueryKey(organizationId, type), "assigned-ids"],
    queryFn: async ({ signal }) => {
      const result = await fetchMembers({
        organizationId,
        type,
        pageIndex: 0,
        pageSize: 200,
        signal,
      });
      return new Set((result.data || []).map((row) => String(row.user_id)));
    },
    enabled: type !== "users",
  });

  const orgUserOptions = useMemo(() => {
    const memberUserIds = assignedIdsQuery.data || new Set();
    return (orgUsersQuery.data || [])
      .filter((row) => !memberUserIds.has(String(row.user_id)))
      .map((row) => ({
        value: String(row.user_id),
        label: orgJoinSelectLabel(row, row.user_id),
      }));
  }, [orgUsersQuery.data, assignedIdsQuery.data]);

  const createUserMutation = useMutation({
    mutationFn: (formData) =>
      axios.post(`/organizations/${organizationId}/create-user/`, formData),
    onSuccess: () => {
      setFormKey((key) => key + 1);
      toast.success(t("created"));
      onSuccess();
    },
    onError: (err) =>
      notifyUserFacingError(err, toast, { source: "org_member_create_user" }),
  });

  async function resolveUserByEmail(email) {
    const { data } = await axios.post(`/users/find-by-email/`, { email });
    const user = data.response[0];
    if (!user?.id) {
      throw t("invite_user_not_found");
    }
    return user;
  }

  const addUserMutation = useMutation({
    mutationFn: async (formData) => {
      const user = await resolveUserByEmail(formData.email);
      return axios.post(`/organizations/${organizationId}/users/`, {
        user_id: user.id,
      });
    },
    onSuccess: () => {
      setFormKey((key) => key + 1);
      toast.success(t("updated"));
      onSuccess();
    },
    onError: (err) =>
      notifyUserFacingError(err, toast, { source: "org_member_add_user" }),
  });

  const inviteUserMutation = useMutation({
    mutationFn: async (formData) => {
      const user = await resolveUserByEmail(formData.email);
      return axios.post(`/organizations/${organizationId}/invite-user/`, {
        user_id: user.id,
      });
    },
    onSuccess: () => {
      setFormKey((key) => key + 1);
      toast.success(t("updated"));
      onSuccess();
    },
    onError: (err) =>
      notifyUserFacingError(err, toast, { source: "org_member_invite_user" }),
  });

  const addMemberMutation = useMutation({
    mutationFn: (userId) =>
      axios.post(config.create(organizationId), { user_id: userId }),
    onSuccess: () => {
      setSelectedUserId(null);
      toast.success(t("updated"));
      onSuccess();
    },
    onError: (err) =>
      notifyUserFacingError(err, toast, { source: "org_member_add_role" }),
  });

  const createFields = [
    {
      name: "first_name",
      label: t("first_name"),
      initialValue: "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "last_name",
      label: t("last_name"),
      initialValue: "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "email",
      label: t("email"),
      initialValue: "",
      required: true,
      schema: () =>
        Yup.string()
          .email(t("validation:email_invalid"))
          .required(t("validation:required")),
    },
  ];

  const emailFields = [
    {
      name: "email",
      label: t("email"),
      initialValue: "",
      required: true,
      schema: () =>
        Yup.string()
          .email(t("validation:email_invalid"))
          .required(t("validation:required")),
    },
  ];

  const userModeSubmit = {
    create: (formData) => createUserMutation.mutate(formData),
    add: (formData) => addUserMutation.mutate(formData),
    invite: (formData) => inviteUserMutation.mutate(formData),
  };

  return (
    <Card>
      <Title order={5} mb="sm">
        {t("add")} {t(config.titleKey)}
      </Title>
      {type === "users" ? (
        <Stack gap="sm">
          <SegmentedControl
            value={userMode}
            onChange={setUserMode}
            data={[
              { label: t("add"), value: "add" },
              { label: t("create"), value: "create" },
              { label: t("invite"), value: "invite" },
            ]}
          />
          <BasicForm
            key={`${userMode}-${formKey}`}
            loading={
              createUserMutation.isPending ||
              addUserMutation.isPending ||
              inviteUserMutation.isPending
            }
            onSubmit={userModeSubmit[userMode]}
            fields={userMode === "create" ? createFields : emailFields}
            buttonProps={{ fullWidth: true }}
          />
        </Stack>
      ) : (
        <Stack gap="sm">
          <Select
            searchable
            clearable
            placeholder={t("user_singular")}
            data={orgUserOptions}
            value={selectedUserId}
            onChange={setSelectedUserId}
            disabled={orgUsersQuery.isLoading || assignedIdsQuery.isLoading}
          />
          <Button
            leftSection={<IconPlus size={14} />}
            disabled={!selectedUserId}
            loading={addMemberMutation.isPending}
            onClick={() => addMemberMutation.mutate(selectedUserId)}
            fullWidth
          >
            {t("add")}
          </Button>
        </Stack>
      )}
    </Card>
  );
}
